import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

/**
 * ConsolidationPanel — embedded in the ShippingTab of an order detail page.
 * Lets an admin combine this order with other consolidatable orders for the
 * same dealer into a single shipment, or view/manage the existing shipment
 * this order is part of.
 *
 * Two modes:
 *   1. Order is NOT in a shipment → show "Consolidate with..." picker of
 *      sibling orders (same dealer, eligible status, not already grouped).
 *   2. Order IS in a shipment → show shipment summary + actions (add more,
 *      remove this order, set quote, book, ship).
 */
interface Props {
  orderId: string;
  dealerId: string;
  shipmentId: string | null;
  onChange?: () => void; // notify parent to reload order
}

export function ConsolidationPanel({ orderId, dealerId, shipmentId, onChange }: Props) {
  const [shipment, setShipment] = useState<any | null>(null);
  const [eligible, setEligible] = useState<any[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [editFreight, setEditFreight] = useState<Record<string, string>>({});

  const saveOrderFreight = async (oid: string) => {
    if (!shipmentId) return;
    const val = parseFloat(editFreight[oid]);
    if (!(val >= 0)) { setError('Enter a valid freight amount'); return; }
    setBusy(true);
    try {
      await api.shipments.setOrderFreight(shipmentId, oid, val);
      setEditFreight(prev => { const n = { ...prev }; delete n[oid]; return n; });
      await refresh();
      onChange?.();
    } catch (e: any) {
      setError(e?.message || 'Failed to update freight');
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setError(null);
    try {
      if (shipmentId) {
        const s = await api.shipments.get(shipmentId);
        setShipment(s);
        const e = await api.shipments.consolidatable(dealerId, shipmentId);
        setEligible(e.filter((o: any) => o.id !== orderId && o.shipment_id !== shipmentId));
      } else {
        setShipment(null);
        const e = await api.shipments.consolidatable(dealerId);
        setEligible(e.filter((o: any) => o.id !== orderId));
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [shipmentId, dealerId, orderId]);

  const togglePick = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const createConsolidation = async () => {
    if (picked.size < 1) return;
    setBusy(true);
    setError(null);
    try {
      const orderIds = [orderId, ...Array.from(picked)];
      await api.shipments.create({ dealerId, orderIds });
      setPicked(new Set());
      onChange?.();
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const addPicked = async () => {
    if (!shipmentId || picked.size < 1) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of picked) {
        await api.shipments.addOrder(shipmentId, id);
      }
      setPicked(new Set());
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const removeFromShipment = async () => {
    if (!shipmentId) return;
    if (!confirm('Remove this order from the consolidated shipment?')) return;
    setBusy(true);
    setError(null);
    try {
      await api.shipments.removeOrder(shipmentId, orderId);
      onChange?.();
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const cancelShipment = async () => {
    if (!shipmentId) return;
    if (!confirm('Cancel this consolidated shipment? All orders will go back to standalone freight.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.shipments.cancel(shipmentId);
      onChange?.();
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  // Mode 1: order is in a shipment
  if (shipment) {
    const statusColor: Record<string, string> = {
      building: 'bg-amber-100 text-amber-800 border-amber-300',
      quoted: 'bg-blue-100 text-blue-800 border-blue-300',
      booked: 'bg-purple-100 text-purple-800 border-purple-300',
      shipped: 'bg-green-100 text-green-800 border-green-300',
      delivered: 'bg-gray-100 text-gray-800 border-gray-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    const editable = ['building', 'quoted'].includes(shipment.status);
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-purple-900">Part of consolidated shipment</h3>
            <p className="text-xs text-purple-700 mt-0.5">
              {shipment.orderCount ?? shipment.orders?.length ?? 0} orders · {shipment.totalItemCount ?? 0} items
              {shipment.freightTotalAmount != null && (
                <> · ${shipment.freightTotalAmount.toFixed(2)} total freight</>
              )}
            </p>
          </div>
          <span className={`text-xs px-2 py-1 rounded border font-medium ${statusColor[shipment.status] || ''}`}>
            {shipment.status.toUpperCase()}
          </span>
        </div>

        <div className="bg-white rounded border border-purple-100 p-3 mb-3">
          <div className="text-xs font-medium text-gray-700 mb-2">Orders in this shipment:</div>
          <div className="space-y-1">
            {(shipment.orders || []).map((o: any) => {
              const isThis = o.id === orderId;
              const label = (
                <>
                  {o.order_number || o.orderNumber}{o.sidemark ? ` — ${o.sidemark}` : ''} ({o.item_count ?? o.itemCount} items)
                  {isThis && ' ← this order'}
                </>
              );
              return (
                <div key={o.id} className="flex justify-between text-xs">
                  {isThis ? (
                    <span className="font-bold">{label}</span>
                  ) : (
                    <Link to={`/orders/${o.id}`} className="text-blue-700 hover:text-blue-900 hover:underline">
                      {label}
                    </Link>
                  )}
                  {editable ? (
                    <span className="flex items-center gap-1">
                      <span className="text-gray-400 text-[11px]">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editFreight[o.id] ?? (o.freight_share != null ? parseFloat(o.freight_share).toFixed(2) : '')}
                        onChange={(e) => setEditFreight(prev => ({ ...prev, [o.id]: e.target.value }))}
                        className="w-20 border border-gray-300 rounded px-1 py-0.5 text-xs text-right"
                        title="Edit this order's freight (locks it)"
                      />
                      {editFreight[o.id] !== undefined && (
                        <button onClick={() => saveOrderFreight(o.id)} disabled={busy}
                          className="text-[11px] px-1.5 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                          Save
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      {o.freight_share != null ? `$${parseFloat(o.freight_share).toFixed(2)}` : '—'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {editable && (
          <>
            <div className="flex flex-wrap gap-2 mb-2">
              <button
                onClick={removeFromShipment}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50"
              >
                Remove this order
              </button>
              <button
                onClick={cancelShipment}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
              >
                Cancel shipment
              </button>
              {eligible.length > 0 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50"
                >
                  {expanded ? 'Hide' : 'Add more orders'} ({eligible.length} eligible)
                </button>
              )}
            </div>

            {expanded && eligible.length > 0 && (
              <div className="bg-white border border-purple-200 rounded p-3">
                <div className="text-xs font-medium text-gray-700 mb-2">Eligible orders to add:</div>
                <div className="space-y-1 max-h-48 overflow-auto">
                  {eligible.map((o: any) => {
                    const isWillCall = o.shipping_method === 'will_call' || o.shipping_method === 'willcall';
                    const isParts = o.order_type === 'parts';
                    return (
                      <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={picked.has(o.id)}
                          onChange={() => togglePick(o.id)}
                          disabled={busy}
                        />
                        <span>
                          {o.order_number}{o.sidemark ? ` — ${o.sidemark}` : ''} ({o.item_count} items, {o.status})
                          {isParts && (
                            <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-semibold border border-purple-200">Parts</span>
                          )}
                          {isWillCall && (
                            <span
                              title="Will be converted from Will-Call to Freight when added to the shipment"
                              className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-semibold border border-amber-200"
                            >WC → Freight</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <button
                  onClick={addPicked}
                  disabled={busy || picked.size === 0}
                  className="mt-2 px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Add {picked.size} order{picked.size === 1 ? '' : 's'}
                </button>
              </div>
            )}
          </>
        )}

        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  // Mode 2: order is standalone — show consolidation invitation
  if (eligible.length === 0) {
    return null; // No siblings, no panel
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-blue-900">Consolidate this order with others</h3>
          <p className="text-xs text-blue-700 mt-0.5">
            {eligible.length} other order{eligible.length === 1 ? '' : 's'} from this dealer can ship together.
          </p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="px-3 py-1.5 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100"
        >
          {expanded ? 'Cancel' : 'Choose orders'}
        </button>
      </div>

      {expanded && (
        <div className="bg-white border border-blue-200 rounded p-3 mt-2">
          <div className="text-xs font-medium text-gray-700 mb-2">Pick orders to ship with this one:</div>
          <div className="space-y-1 max-h-48 overflow-auto">
            {eligible.map((o: any) => {
              const isWillCall = o.shipping_method === 'will_call' || o.shipping_method === 'willcall';
              const isParts = o.order_type === 'parts';
              return (
                <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={picked.has(o.id)}
                    onChange={() => togglePick(o.id)}
                    disabled={busy}
                  />
                  <span>
                    {o.order_number}{o.sidemark ? ` — ${o.sidemark}` : ''} ({o.item_count} items, {o.status})
                    {isParts && (
                      <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-semibold border border-purple-200">Parts</span>
                    )}
                    {isWillCall && (
                      <span
                        title="Will be converted from Will-Call to Freight when added to the shipment"
                        className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-semibold border border-amber-200"
                      >WC → Freight</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <button
            onClick={createConsolidation}
            disabled={busy || picked.size === 0}
            className="mt-2 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Create shipment with {picked.size + 1} order{picked.size === 0 ? '' : 's'}
          </button>
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
}
